/**
 * Core types for Almadar integrations
 */

/**
 * Configuration for an integration instance
 */
export interface IntegrationConfig {
  /** Integration name (matches registry) */
  name: string;

  /** Environment variables (API keys, secrets) */
  env: Record<string, string>;

  /** Optional logger */
  logger?: IntegrationLogger;

  /** Optional rate limiting config */
  rateLimit?: {
    requestsPerSecond: number;
    burstSize: number;
  };

  /** Optional timeout (ms) */
  timeout?: number;

  /** Optional retry config */
  retry?: {
    maxAttempts: number;
    backoffMs: number;
    maxBackoffMs?: number;
  };
}

/**
 * Result of an integration action call
 */
export interface IntegrationResult<T = unknown> {
  /** Success flag */
  success: boolean;

  /** Response data (on success) */
  data?: T;

  /** Error (on failure) */
  error?: IntegrationError;

  /** Metadata (timing, retries, etc.) */
  metadata: {
    integration: string;
    action: string;
    duration: number;
    retries: number;
    timestamp: number;
  };
}

/**
 * Integration error codes
 */
export type IntegrationErrorCode =
  | 'VALIDATION_ERROR'
  | 'AUTH_ERROR'
  | 'RATE_LIMIT_ERROR'
  | 'TIMEOUT_ERROR'
  | 'NETWORK_ERROR'
  | 'SERVICE_ERROR'
  | 'UNKNOWN_ERROR';

/**
 * Integration error
 */
export class IntegrationError extends Error {
  code: IntegrationErrorCode;
  integration?: string;
  action?: string;
  details?: unknown;

  constructor(
    message: string,
    code: IntegrationErrorCode = 'UNKNOWN_ERROR',
    details?: unknown,
  ) {
    super(message);
    this.name = 'IntegrationError';
    this.code = code;
    this.details = details;
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      integration: this.integration,
      action: this.action,
      details: this.details,
    };
  }
}

import type { LogMeta } from '@almadar/core';

/** Re-export LogMeta from @almadar/core as the canonical log metadata type. */
export type { LogMeta };

/**
 * Logger interface
 */
export interface IntegrationLogger {
  debug(message: string, meta?: LogMeta): void;
  info(message: string, meta?: LogMeta): void;
  warn(message: string, meta?: LogMeta): void;
  error(message: string, meta?: LogMeta): void;
}

/**
 * Integration action parameters.
 * Each integration's execute() method receives params as this type.
 * Individual methods cast to specific param shapes from their contracts.
 *
 * Per-key value union is widened to admit `@almadar/core`'s `FieldValue`
 * shape (entity / payload values flowing into call-service args): `Date`
 * for date fields, `IntegrationParams[]` for nested object arrays, and a
 * raw-value array catch-all so `string[]` / `number[]` / mixed payload
 * arrays satisfy the index signature without requiring `as unknown as`
 * casts at the call site. Adapters narrow these to their canonical input
 * types (typically JSON-string serialisation for `Date`).
 */
export type IntegrationParamValue =
  | string
  | number
  | boolean
  | Date
  | null
  | undefined
  | ReadonlyArray<IntegrationParamValue>
  | { readonly [key: string]: IntegrationParamValue };

export interface IntegrationParams {
  [key: string]: IntegrationParamValue;
}

/**
 * Validation result
 */
export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

/**
 * Validation error
 */
export interface ValidationError {
  param: string;
  message: string;
}
